/**
 * OWC accessibility helpers (ADA remediation, Oct 2026)
 *
 * Carousel slides that Horizon hides from screen readers (aria-hidden="true") still
 * contained focusable links, so keyboard users could tab into products they couldn't see
 * and screen readers had nothing to announce (axe: aria-hidden-focus). This keeps the
 * `inert` property in sync with aria-hidden on every <slideshow-slide>, including slides
 * added later (filters, quick add, section re-renders).
 *
 * Also:
 * - Escape closes the desktop mega menu and leaves focus on its top-level link. Horizon opens
 *   the menu on focus but had no way to dismiss it from the keyboard (WCAG 1.4.13).
 * - Yotpo's star rating number uses the brand tan (#A18A78, about 3.2:1 on white). It is
 *   darkened to #7A6555 (5.5:1) here because Yotpo's own settings don't expose that color.
 * - Crossed-out compare-at prices were 40% opacity (about 2.6:1); raised to 70% (about 7:1).
 * - The mobile sticky add-to-cart button now keeps its "Add to cart" text for screen readers.
 * - Closing the mobile menu drawer returns focus to the Menu button instead of the page body.
 * - Slideshow dots/thumbnails: aria-selected (not valid on buttons) becomes aria-current, and dots get 24px targets.
 * - Rocket Dog only: rating number, Affirm text and crossed-out price contrast, breadcrumb target size.
 */
(() => {
  const SELECTOR = 'slideshow-slide';

  /** @param {Element} el */
  const sync = (el) => {
    const hidden = el.getAttribute('aria-hidden') === 'true';
    if (el.inert !== hidden) el.inert = hidden;
  };

  /** @param {ParentNode} root */
  const syncAll = (root) => {
    root.querySelectorAll(SELECTOR).forEach(sync);
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        if (mutation.target instanceof Element && mutation.target.matches(SELECTOR)) sync(mutation.target);
        continue;
      }
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(SELECTOR)) sync(node);
        syncAll(node);
      });
    }
  });

  const start = () => {
    syncAll(document);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

(() => {
  // Slideshow dots and thumbnails: Horizon marks the active one with aria-selected, which is not
  // allowed on a plain button, so screen readers never hear which slide is showing. Swap it for
  // aria-current="true" ("current item") every time the slideshow updates it.
  const SELECTOR = 'slideshow-controls .slideshow-control';

  /** @param {Element} el */
  const fix = (el) => {
    if (!el.hasAttribute('aria-selected')) return;
    const isCurrent = el.getAttribute('aria-selected') === 'true';
    el.removeAttribute('aria-selected');
    if (isCurrent) el.setAttribute('aria-current', 'true');
    else el.removeAttribute('aria-current');
  };

  const fixAll = (root) => root.querySelectorAll(SELECTOR).forEach(fix);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        if (mutation.target instanceof Element && mutation.target.matches(SELECTOR)) fix(mutation.target);
        continue;
      }
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) fixAll(node);
      });
    }
  });

  const start = () => {
    fixAll(document);
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-selected'] });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

(() => {
  // Escape closes an open desktop mega menu
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
    const menu = event.target.closest('header-menu');
    if (!menu) return;
    const item = event.target.closest('.menu-list__list-item');
    const link = item?.querySelector(':scope > .menu-list__link');
    if (!link || link.getAttribute('aria-expanded') !== 'true') return;

    if (document.activeElement !== link) link.focus();
    if (typeof menu.deactivate === 'function') menu.deactivate({ target: link });
    event.preventDefault();
  });

  // Mobile menu drawer: when it (or one of its submenus) closes, Horizon drops focus on the
  // page body. Put it back on the button that opened it.
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || details.open) return;
      if (!details.closest('header-drawer')) return;
      const summary = details.querySelector(':scope > summary');
      const active = document.activeElement;
      if (summary && (!active || active === document.body || details.contains(active))) {
        summary.focus();
      }
    },
    true
  );

  // Contrast and naming fixes that only need CSS
  const style = document.createElement('style');
  style.textContent = [
    // Yotpo rating number: brand tan was 3.2:1, this is 5.5:1
    '.yotpo-sr-bottom-line-left-panel, .yotpo-sr-bottom-line-text { color: #7A6555 !important; }',
    // Crossed-out "was" prices were 40% opacity (about 2.6:1); 70% is about 7:1
    '.compare-at-price { opacity: 0.7 !important; }',
    // Mobile sticky add-to-cart hid its text with display:none, leaving an unnamed icon button.
    // Keep the text available to screen readers while it stays visually hidden.
    '@media screen and (max-width: 749px) { .sticky-add-to-cart__bar .add-to-cart-text__content { display: block !important; position: absolute !important; width: 1px !important; height: 1px !important; overflow: hidden !important; clip: rect(0 0 0 0) !important; clip-path: inset(50%) !important; white-space: nowrap !important; } }',
    // Rocket Dog: review star count number on product cards (#857c7c was 4.1:1; this is 5.8:1)
    '.rating-wrapper .rating-count { color: #6B6363 !important; }',
    // Rocket Dog: Affirm "as low as" text was #808080 (3.9:1); this is 7:1
    // (extra selectors outrank Affirm's own !important rule, which loads later in the page)
    'html body .affirm-as-low-as, html body .affirm-as-low-as *, html body .affirm-modal-trigger { color: #595959 !important; }',
    // Rocket Dog: crossed-out prices are a lighter grey than KDB's, so 70% opacity still read 3.4:1.
    // Solid #6B6B6B is 5.3:1.
    '.compare-at-price { opacity: 1 !important; color: #6B6B6B !important; }',
    // Rocket Dog: breadcrumb links were 19px tall; WCAG 2.2 asks for 24px click targets
    '.owc_breadcrumbs-list a { display: inline-block; min-height: 24px; line-height: 24px; }',
    // Slideshow dots/thumbnails now use aria-current (see above); keep the active styling.
    'slideshow-controls .slideshow-controls__dots button[aria-current="true"] { --color: var(--color-active); }',
    'slideshow-controls .slideshow-controls__dots button[aria-current="true"]::after { box-shadow: inset 0 0 0 1.5px hsl(0 0% 0% / 0.4); }',
    'slideshow-controls .slideshow-controls__thumbnail[aria-current="true"] { outline: var(--focus-outline-width) solid currentcolor; outline-offset: calc(var(--focus-outline-offset) / 2); border: var(--style-border-width) solid rgb(var(--color-border-rgb) / var(--media-border-opacity)); }',
    // Slideshow dots were 18px tap targets; 24px meets WCAG 2.2 (the visible dot stays the same size)
    'slideshow-controls .slideshow-controls__dots button { min-width: 24px; min-height: 24px; margin: -3px; }',
  ].join('\n');
  document.head.append(style);
})();
